/**
 * Doctor onboarding API service.
 * Same endpoints and payloads as Android/iOS - no deviation.
 */
import { apiClient } from '../api/apiClient';
import type {
  DoctorProfile,
  DoctorEducation,
  DoctorCertification,
  DoctorDocument,
  DoctorPractice,
  DoctorPracticeTiming,
  DoctorService,
  DoctorBankDetails,
  DoctorBankDetailsInput,
  CountryCode,
  Timezone,
  GenderType,
  Specialization,
  Subspecialization,
  MedicalCouncil,
  CouncilInfo,
  DegreeType,
  PracticeType,
  CertificationType,
  ServiceCategory,
  MedicalService,
} from '../../types/doctorOnboarding';

const BASE = '/api/v1';
const PROFILE_BASE = `${BASE}/doctors/profile`;

// ----- Doctor identity (for profile create) -----
export async function getCurrentDoctor(): Promise<{ id: number; email: string; is_verified: boolean }> {
  const { data } = await apiClient.get<{ id: number; email: string; is_verified: boolean }>(
    `${BASE}/doctors/me`
  );
  return data;
}

// ----- Profile (Steps 1–2) -----
export async function getProfile(): Promise<DoctorProfile> {
  const { data } = await apiClient.get<DoctorProfile>(`${PROFILE_BASE}/profile`);
  return data;
}

export async function createOrUpdateProfile(body: DoctorProfile): Promise<DoctorProfile> {
  const { data } = await apiClient.post<DoctorProfile>(`${PROFILE_BASE}/profile`, body);
  return data;
}

export async function updateProfilePartial(
  body: Partial<DoctorProfile>
): Promise<DoctorProfile> {
  const { data } = await apiClient.patch<DoctorProfile>(`${PROFILE_BASE}/profile`, body);
  return data;
}

export async function updateOnboardingStep(step: number): Promise<void> {
  await apiClient.patch(`${PROFILE_BASE}/profile/status`, {
    onboarding_step: step,
  });
}

export async function getProfileCompletion(): Promise<{
  completion_percentage: number;
  completed_steps: number[];
  missing_fields: string[];
  next_step: number;
  can_submit: boolean;
  onboarding_status?: string;
  current_step?: number;
}> {
  const { data } = await apiClient.get(`${PROFILE_BASE}/profile/completion`);
  return data as any;
}

// ----- Education -----
export async function getEducation(): Promise<DoctorEducation[]> {
  const { data } = await apiClient.get<DoctorEducation[]>(`${PROFILE_BASE}/education`);
  return Array.isArray(data) ? data : [];
}

export async function addEducation(
  body: Omit<DoctorEducation, 'id'> & { certificate_document_url: string }
): Promise<DoctorEducation> {
  const { data } = await apiClient.post<DoctorEducation>(`${PROFILE_BASE}/education`, body);
  return data;
}

export async function updateEducation(
  id: number,
  body: Partial<DoctorEducation>
): Promise<DoctorEducation> {
  const { data } = await apiClient.put<DoctorEducation>(
    `${PROFILE_BASE}/education/${id}`,
    body
  );
  return data;
}

export async function deleteEducation(id: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/education/${id}`);
}

// ----- Certifications -----
export async function getCertifications(): Promise<DoctorCertification[]> {
  const { data } = await apiClient.get<DoctorCertification[]>(
    `${PROFILE_BASE}/certifications`
  );
  return Array.isArray(data) ? data : [];
}

export async function addCertification(
  body: Omit<DoctorCertification, 'id'>
): Promise<DoctorCertification> {
  const { data } = await apiClient.post<DoctorCertification>(
    `${PROFILE_BASE}/certifications`,
    body
  );
  return data;
}

export async function updateCertification(
  id: number,
  body: Partial<DoctorCertification>
): Promise<DoctorCertification> {
  const { data } = await apiClient.put<DoctorCertification>(
    `${PROFILE_BASE}/certifications/${id}`,
    body
  );
  return data;
}

export async function deleteCertification(id: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/certifications/${id}`);
}

// ----- Documents -----
export async function getDocuments(): Promise<DoctorDocument[]> {
  const { data } = await apiClient.get<DoctorDocument[]>(`${PROFILE_BASE}/documents`);
  return Array.isArray(data) ? data : [];
}

export async function uploadDocument(
  file: File,
  documentType: string,
  description?: string
): Promise<DoctorDocument> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  if (description) formData.append('description', description);
  const { data } = await apiClient.post<DoctorDocument>(
    `${PROFILE_BASE}/documents`,
    formData
  );
  return data;
}

export async function deleteDocument(id: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/documents/${id}`);
}

export async function updateDocument(
  id: number,
  body: Partial<DoctorDocument>
): Promise<DoctorDocument> {
  const { data } = await apiClient.put<DoctorDocument>(
    `${PROFILE_BASE}/documents/${id}`,
    body
  );
  return data;
}

// ----- Practices -----
export async function getPractices(): Promise<DoctorPractice[]> {
  const { data } = await apiClient.get<DoctorPractice[]>(`${PROFILE_BASE}/practices`);
  return Array.isArray(data) ? data : [];
}

export async function addPractice(
  body: Omit<DoctorPractice, 'id' | 'created_at' | 'updated_at'>
): Promise<DoctorPractice> {
  const { data } = await apiClient.post<DoctorPractice>(`${PROFILE_BASE}/practices`, body);
  return data;
}

export async function updatePractice(
  id: number,
  body: Partial<DoctorPractice>
): Promise<DoctorPractice> {
  const { data } = await apiClient.put<DoctorPractice>(
    `${PROFILE_BASE}/practices/${id}`,
    body
  );
  return data;
}

export async function deletePractice(id: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/practices/${id}`);
}

export async function addPracticeTiming(
  practiceId: number,
  body: Omit<DoctorPracticeTiming, 'id' | 'practice_id' | 'created_at' | 'updated_at'>
): Promise<DoctorPracticeTiming> {
  const { data } = await apiClient.post<DoctorPracticeTiming>(
    `${PROFILE_BASE}/practices/${practiceId}/timings`,
    { ...body, practice_id: practiceId }
  );
  return data;
}

export async function getPracticeTimings(practiceId: number): Promise<DoctorPracticeTiming[]> {
  const { data } = await apiClient.get<DoctorPracticeTiming[]>(
    `${PROFILE_BASE}/practices/${practiceId}/timings`
  );
  return Array.isArray(data) ? data : [];
}

export async function updatePracticeTiming(
  practiceId: number,
  timingId: number,
  body: Partial<Omit<DoctorPracticeTiming, 'id' | 'practice_id' | 'created_at' | 'updated_at'>>
): Promise<DoctorPracticeTiming> {
  const { data } = await apiClient.put<DoctorPracticeTiming>(
    `${PROFILE_BASE}/practices/${practiceId}/timings/${timingId}`,
    body
  );
  return data;
}

export async function deletePracticeTiming(practiceId: number, timingId: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/practices/${practiceId}/timings/${timingId}`);
}

// ----- Services -----
export async function getServices(): Promise<DoctorService[]> {
  const { data } = await apiClient.get<DoctorService[]>(`${PROFILE_BASE}/services`);
  return Array.isArray(data) ? data : [];
}

export async function addService(
  body: Omit<DoctorService, 'id' | 'created_at' | 'updated_at'>
): Promise<DoctorService> {
  const { data } = await apiClient.post<DoctorService>(`${PROFILE_BASE}/services`, body);
  return data;
}

export async function updateService(
  id: number,
  body: Partial<DoctorService>
): Promise<DoctorService> {
  const { data } = await apiClient.put<DoctorService>(
    `${PROFILE_BASE}/services/${id}`,
    body
  );
  return data;
}

export async function deleteService(id: number): Promise<void> {
  await apiClient.delete(`${PROFILE_BASE}/services/${id}`);
}

// ----- Bank details -----
export async function getBankDetails(): Promise<DoctorBankDetails | null> {
  try {
    const { data } = await apiClient.get<DoctorBankDetails>(
      `${PROFILE_BASE}/bank-details`
    );
    return data;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function createOrUpdateBankDetails(
  body: DoctorBankDetailsInput
): Promise<DoctorBankDetails> {
  const { data } = await apiClient.post<DoctorBankDetails>(
    `${PROFILE_BASE}/bank-details`,
    body
  );
  return data;
}

export async function updateBankDetails(
  body: Partial<DoctorBankDetails>
): Promise<DoctorBankDetails> {
  const { data } = await apiClient.patch<DoctorBankDetails>(
    `${PROFILE_BASE}/bank-details`,
    body
  );
  return data;
}

// ----- Onboarding submit & status -----
export async function submitOnboarding(): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(
    `${PROFILE_BASE}/submit`
  );
  return data;
}

export async function getOnboardingStatus(): Promise<{
  /** Doctor onboarding state: pending_email | onboarding_incomplete | submitted | approved | rejected */
  onboarding_status?: string;
  verification_notes?: string | null;
  next_action?: string;
  current_step?: number;
  can_submit?: boolean;
}> {
  const { data } = await apiClient.get(`${PROFILE_BASE}/status`);
  return data as any;
}

export async function getOnboardingSummary(): Promise<{
  profile?: DoctorProfile;
  can_submit?: boolean;
  missing_requirements?: string[];
  completion_percentage?: number;
}> {
  const { data } = await apiClient.get(`${PROFILE_BASE}/summary`);
  return data as any;
}

// ----- File upload (profile photo, certificates) -----
export async function uploadFile(
  file: File,
  documentType: string
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  const { data } = await apiClient.post<{ url: string }>(`${BASE}/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ----- Dictionaries (same as mobile) -----
export async function getCountryCodes(): Promise<CountryCode[]> {
  const { data } = await apiClient.get<CountryCode[]>(`${BASE}/country-codes/`);
  return Array.isArray(data) ? data : [];
}

export async function getTimezones(): Promise<Timezone[]> {
  const { data } = await apiClient.get<Timezone[]>(`${BASE}/timezones/`);
  return Array.isArray(data) ? data : [];
}

export async function getGenderTypes(): Promise<GenderType[]> {
  const { data } = await apiClient.get<GenderType[]>(`${BASE}/gender-types/`);
  return Array.isArray(data) ? data : [];
}

export async function getSpecializations(): Promise<Specialization[]> {
  const { data } = await apiClient.get<{ specializations: Specialization[] }>(
    `${BASE}/specializations/`
  );
  return data?.specializations ?? [];
}

export async function getSubspecializations(specializationId: number): Promise<Subspecialization[]> {
  const { data } = await apiClient.get<Subspecialization[]>(
    `${BASE}/specializations/${specializationId}/subspecializations`
  );
  return Array.isArray(data) ? data : [];
}

export async function getCouncilInfo(specializationId: number): Promise<CouncilInfo> {
  const { data } = await apiClient.get<CouncilInfo>(
    `${BASE}/specializations/${specializationId}/councils`
  );
  return data;
}

export async function getAllMedicalCouncils(): Promise<MedicalCouncil[]> {
  const { data } = await apiClient.get<MedicalCouncil[]>(
    `${BASE}/specializations/councils/all`
  );
  return Array.isArray(data) ? data : [];
}

export async function getDegreeTypes(): Promise<DegreeType[]> {
  const { data } = await apiClient.get<DegreeType[]>(`${BASE}/degree-types/`);
  return Array.isArray(data) ? data : [];
}

export async function getPracticeTypes(): Promise<PracticeType[]> {
  const { data } = await apiClient.get<PracticeType[]>(`${BASE}/practice-types/`);
  return Array.isArray(data) ? data : [];
}

export async function getCertificationTypes(): Promise<CertificationType[]> {
  const { data } = await apiClient.get<CertificationType[]>(
    `${BASE}/certification-types/`
  );
  return Array.isArray(data) ? data : [];
}

export async function getServiceCategories(): Promise<ServiceCategory[]> {
  const { data } = await apiClient.get<ServiceCategory[]>(
    `${BASE}/doctors/services/categories`
  );
  return Array.isArray(data) ? data : [];
}

export async function getMedicalServicesDictionary(): Promise<MedicalService[]> {
  const { data } = await apiClient.get<MedicalService[]>(
    `${BASE}/doctors/services/dictionary`
  );
  return Array.isArray(data) ? data : [];
}
